import React, { useState, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";

const CheckboxGroup = forwardRef(
  (
    { group, handleChange, value, handleSelectAll, handleDeselectAll, index },
    ref
  ) => {
    const { t: translate, i18n } = useTranslation();

    const [searchTerm, setSearchTerm] = useState("");

    const handleSearchChange = (e) => {
      setSearchTerm(e.target.value.toLowerCase());
    };

    const filterCheckboxes = (checkboxes) => {
      return checkboxes.filter((checkbox) => {
        const matchesParent = checkbox.displayName
          .toLowerCase()
          .includes(searchTerm);
        const matchesChildren = checkbox.children?.some((child) =>
          child.displayName.toLowerCase().includes(searchTerm)
        );
        return matchesParent || matchesChildren;
      });
    };

    const filteredValues = filterCheckboxes(group.values);

    const handleSelectAllInner = (e, parent) => {
      return handleSelectAll(parent.children, group);
    };
    const handleDeselectAllInner = (e, parent) => {
      return handleDeselectAll(parent.children, group);
    };

    return (
      <>
        <label className="font-bold rtl:text-right">{group.displayName}</label>
        {group.searchable && (
          <div className="relative mb-3">
            <span className="absolute pt-[10px] left-0 pl-3 flex items-center pointer-events-none">
              <Search size={18} />
            </span>
            <input
              type="text"
              placeholder={translate("shared.search")}
              value={searchTerm}
              onChange={handleSearchChange}
              className="p-2 pl-10 border border-gray-300 rounded w-full"
              tabIndex={index}
            />
          </div>
        )}
        {group.name && (
          <span className="text-xs border-b-2 border-gray-100 py-1 rtl:text-right">
            {group.name}
          </span>
        )}
        <div
          data-testid={group.testKey || group.key}
          className="flex flex-col gap-3 pl-1"
        >
          {!!filteredValues?.length ? (
            filteredValues.map((parent) => {
              const allChildValues = parent?.children?.map(
                (child) => child?.value
              );
              const checkedChildValues = value[group.key]?.value || "";
              const splitValues =
                checkedChildValues !== "" && checkedChildValues.split(",");
              const areAllSiblingsChecked =
                allChildValues?.every(
                  (childValue) =>
                    splitValues && splitValues?.includes(childValue)
                ) || Object.values(checkedChildValues).includes(parent.value);

              return (
                <div
                  key={`${parent.value}_${areAllSiblingsChecked}`}
                  className="flex flex-col gap-1 "
                >
                  {parent.selectable ? (
                    <div className="flex flex-col">
                      <label className="inline-flex items-center">
                        <input
                          data-testid={parent.displayName}
                          key={group.key}
                          type="checkbox"
                          value={parent.value}
                          className="form-checkbox text-primary-500 h-4 w-4 cursor-pointer"
                          {...(areAllSiblingsChecked && {
                            checked: true,
                          })}
                          onChange={(e) =>
                            handleChange(e, parent, false, group)
                          }
                          tabIndex={index}
                        />
                        <span className="rtl:mr-2 ltr:ml-2">
                          {parent.displayName}
                        </span>
                      </label>
                      <div className="text-sm ml-6">{parent.description}</div>
                    </div>
                  ) : (
                    <div className="flex flex-col">
                      <div className="flex items-center justify-between">
                        {parent.displayName !== "All" && (
                          <span>{parent.displayName}</span>
                        )}
                        <div className="flex gap-2">
                          <button
                            className="text-xs text-primary-500 hover:underline"
                            onClick={(e) => handleSelectAllInner(e, parent)}
                            tabIndex={index}
                          >
                            {translate("filters.select-all")}
                          </button>
                          <button
                            className="text-xs text-primary-500 hover:underline"
                            onClick={(e) => handleDeselectAllInner(e, parent)}
                            tabIndex={index}
                          >
                            {translate("filters.deselect-all")}
                          </button>
                        </div>
                      </div>
                      <div className="text-sm pl-1">{parent.description}</div>
                    </div>
                  )}
                  {parent.children && (
                    <div className="pt-1 max-h-32 overflow-y-auto">
                      {parent.children.map((child, index) => {
                        const isChildVisible = child.displayName
                          .toLowerCase()
                          .includes(searchTerm);
                        if (isChildVisible) {
                          const valuesArray =
                            value[group.key]?.value?.split(",");
                          const checked = valuesArray?.includes(child.value);
                          return (
                            <div key={index} className="flex flex-col">
                              <div
                                key={`${child.value}_${checked}`}
                                className="ltr:ml-4 rtl:mr-4 rtl:text-right"
                              >
                                <label className="inline-flex items-center">
                                  <input
                                    data-testid={child.displayName}
                                    value={child.value}
                                    type="checkbox"
                                    className="form-checkbox text-primary-500 h-4 w-4 cursor-pointer"
                                    {...(checked && {
                                      checked: true,
                                    })}
                                    onChange={(e) =>
                                      handleChange(e, child, true, group)
                                    }
                                    tabIndex={index}
                                  />
                                  <span className="ltr:ml-2 rtl:mr-2">
                                    {child.displayName}
                                  </span>
                                </label>
                              </div>
                              <div className="text-sm ml-11">
                                {child.description}
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <>{translate("filters.no-results")}</>
          )}
        </div>
      </>
    );
  }
);

CheckboxGroup.displayName = "CheckboxGroup";

export default CheckboxGroup;
